import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "prosecours_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(KEY) || "");
  const [input, setInput] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [audits, setAudits] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [articles, setArticles] = useState([]);
  const [artForm, setArtForm] = useState({ title: "", tag: "", excerpt: "", content: "" });
  const [savingArt, setSavingArt] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [tab, setTab] = useState("audits");

  const verify = async (t) => {
    try {
      await axios.post(`${API}/admin/verify`, {}, { headers: { "X-Admin-Token": t } });
      setAuthed(true);
      setToken(t);
      localStorage.setItem(KEY, t);
      await loadAll(t);
    } catch (e) {
      setAuthed(false);
      localStorage.removeItem(KEY);
      toast.error("Token invalide");
    }
  };

  const loadAll = async (t) => {
    setLoading(true);
    try {
      const [a, c, j] = await Promise.all([
        axios.get(`${API}/admin/audit-requests`, { headers: { "X-Admin-Token": t } }),
        axios.get(`${API}/admin/contacts`, { headers: { "X-Admin-Token": t } }),
        axios.get(`${API}/articles`),
      ]);
      setAudits(a.data);
      setContacts(c.data);
      setArticles(j.data);
    } catch (e) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) verify(token);
    // eslint-disable-next-line
  }, []);

  const logout = () => {
    localStorage.removeItem(KEY);
    setAuthed(false);
    setToken("");
    setAudits([]);
    setContacts([]);
    setArticles([]);
  };

  const bodyToText = (body) =>
    (body || [])
      .map((s) => (s.h ? `## ${s.h}\n\n` : "") + s.p.join("\n\n"))
      .join("\n\n");

  const startEdit = (a) => {
    setEditingId(a.id);
    setArtForm({
      title: a.title,
      tag: a.tag,
      excerpt: a.excerpt,
      content: bodyToText(a.body),
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setArtForm({ title: "", tag: "", excerpt: "", content: "" });
  };

  const saveArticle = async () => {
    if (!artForm.title || !artForm.tag || !artForm.excerpt || !artForm.content) {
      toast.error("Titre, rubrique, chapô et contenu sont requis.");
      return;
    }
    setSavingArt(true);
    try {
      if (editingId) {
        const { data } = await axios.put(`${API}/admin/articles/${editingId}`, artForm, {
          headers: { "X-Admin-Token": token },
        });
        setArticles((list) => list.map((a) => (a.id === editingId ? data : a)));
        toast.success("Article mis à jour.");
      } else {
        const { data } = await axios.post(`${API}/admin/articles`, artForm, {
          headers: { "X-Admin-Token": token },
        });
        setArticles((list) => [data, ...list]);
        toast.success("Article publié.");
      }
      cancelEdit();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement.");
    } finally {
      setSavingArt(false);
    }
  };

  const deleteArticle = async (id) => {
    if (!window.confirm("Supprimer définitivement cet article ?")) return;
    try {
      await axios.delete(`${API}/admin/articles/${id}`, {
        headers: { "X-Admin-Token": token },
      });
      setArticles((list) => list.filter((a) => a.id !== id));
      toast.success("Article supprimé.");
    } catch (e) {
      toast.error("Erreur lors de la suppression.");
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#f7f5f0] flex items-center justify-center px-6" data-testid="admin-login">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            verify(input);
          }}
          className="w-full max-w-md"
        >
          <Link to="/" className="font-serif-ed text-2xl text-[#1a1918]">
            ProSecours
          </Link>
          <h1 className="mt-8 font-serif-ed text-5xl md:text-6xl leading-none text-[#1a1918]">
            Espace <span className="italic text-[#ce1e20]">administrateur</span>
          </h1>
          <p className="mt-4 overline text-[#1a1918]/60">
            Accès protégé — jeton requis
          </p>
          <div className="mt-10">
            <label className="overline text-[#1a1918]/60">Jeton</label>
            <input
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              data-testid="admin-token-input"
              className="w-full bg-transparent border-b border-[#1a1918]/30 py-4 text-lg text-[#1a1918] focus:outline-none focus:border-[#1a1918]"
              placeholder="Entrer le jeton"
              autoFocus
            />
          </div>
          <button
            type="submit"
            data-testid="admin-login-submit"
            className="mt-8 inline-flex items-center gap-3 px-8 py-4 bg-[#1a1918] text-[#f7f5f0] overline hover:bg-[#ce1e20] transition-colors"
          >
            Entrer →
          </button>
        </form>
      </div>
    );
  }

  const rows = tab === "contacts" ? contacts : audits;

  return (
    <div className="min-h-screen bg-[#f7f5f0]" data-testid="admin-dashboard">
      <header className="border-b border-[#1a1918]/15 bg-[#f7f5f0]/85 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="font-serif-ed text-2xl text-[#1a1918]">
            ProSecours
          </Link>
          <div className="flex items-center gap-4">
            <button
              onClick={() => loadAll(token)}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 overline text-[#1a1918]/70 hover:text-[#1a1918]"
            >
              <RefreshCw size={14} />
              Rafraîchir
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 overline text-[#1a1918]/70 hover:text-[#ce1e20]"
            >
              <LogOut size={14} />
              Déconnexion
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-12 md:py-20">
        <div className="flex items-baseline justify-between gap-6 flex-wrap">
          <h1 className="font-serif-ed text-5xl md:text-6xl leading-none text-[#1a1918]">
            Boîte de <span className="italic text-[#ce1e20]">réception</span>
          </h1>
          <div className="overline text-[#1a1918]/60">
            {loading ? "Chargement…" : `${audits.length} demandes · ${contacts.length} contacts · ${articles.length} articles`}
          </div>
        </div>

        <div className="mt-10 flex items-center gap-8 border-b border-[#1a1918]/20">
          {[
            { id: "audits", label: `Demandes d'audit (${audits.length})` },
            { id: "contacts", label: `Contacts (${contacts.length})` },
            { id: "journal", label: `Journal (${articles.length})` },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`pb-3 overline transition-colors ${
                tab === t.id
                  ? "text-[#1a1918] border-b-2 border-[#0f2846]"
                  : "text-[#1a1918]/50 hover:text-[#1a1918]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-8 divide-y divide-[#1a1918]/15 border-t border-[#1a1918]/25">
          {tab === "journal" && (
            <div className="py-8 space-y-12">
              <div
                className="border border-[#1a1918]/20 bg-white/60 p-6 md:p-8"
                data-testid="article-form"
              >
                <div className="overline text-[#1a1918]/60 mb-6">
                  {editingId ? "Modifier l'article (l'adresse URL reste inchangée)" : "Publier un nouvel article"}
                </div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                  <div className="col-span-2 md:col-span-1">
                    <label className="overline text-[#1a1918]/60">Titre</label>
                    <input
                      data-testid="article-input-title"
                      className="w-full bg-transparent border-b border-[#1a1918]/30 py-3 text-base text-[#1a1918] placeholder:text-[#1a1918]/40 focus:outline-none focus:border-[#1a1918]"
                      placeholder="Titre de l'article"
                      value={artForm.title}
                      onChange={(e) => setArtForm((f) => ({ ...f, title: e.target.value }))}
                    />
                  </div>
                  <div className="col-span-2 md:col-span-1">
                    <label className="overline text-[#1a1918]/60">Rubrique</label>
                    <input
                      data-testid="article-input-tag"
                      className="w-full bg-transparent border-b border-[#1a1918]/30 py-3 text-base text-[#1a1918] placeholder:text-[#1a1918]/40 focus:outline-none focus:border-[#1a1918]"
                      placeholder="Guide pratique, Comparatif, Chiffres…"
                      value={artForm.tag}
                      onChange={(e) => setArtForm((f) => ({ ...f, tag: e.target.value }))}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="overline text-[#1a1918]/60">Chapô (résumé)</label>
                    <textarea
                      rows={2}
                      data-testid="article-input-excerpt"
                      className="w-full bg-transparent border-b border-[#1a1918]/30 py-3 text-base text-[#1a1918] placeholder:text-[#1a1918]/40 focus:outline-none focus:border-[#1a1918] resize-none"
                      placeholder="Une ou deux phrases d'accroche, affichées dans la liste et sur Google."
                      value={artForm.excerpt}
                      onChange={(e) => setArtForm((f) => ({ ...f, excerpt: e.target.value }))}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="overline text-[#1a1918]/60">Contenu</label>
                    <textarea
                      rows={10}
                      data-testid="article-input-content"
                      className="w-full bg-transparent border border-[#1a1918]/25 mt-2 p-4 text-base leading-relaxed text-[#1a1918] placeholder:text-[#1a1918]/40 focus:outline-none focus:border-[#1a1918]"
                      placeholder={"Premier paragraphe…\n\n## Titre de section\n\nParagraphe suivant…"}
                      value={artForm.content}
                      onChange={(e) => setArtForm((f) => ({ ...f, content: e.target.value }))}
                    />
                    <p className="mt-2 text-xs text-[#1a1918]/50">
                      Une ligne vide = nouveau paragraphe. Une ligne commençant
                      par « ## » = titre de section. La date, le temps de
                      lecture et le référencement sont générés automatiquement.
                    </p>
                  </div>
                </div>
                <button
                  onClick={saveArticle}
                  disabled={savingArt}
                  data-testid="article-create-submit"
                  className="mt-6 inline-flex items-center gap-3 px-8 py-4 bg-[#ce1e20] text-white overline hover:bg-[#a71518] transition-colors disabled:opacity-60"
                >
                  {savingArt
                    ? "Enregistrement…"
                    : editingId
                    ? "Mettre à jour l'article"
                    : "Publier l'article"}
                  <span>→</span>
                </button>
                {editingId && (
                  <button
                    onClick={cancelEdit}
                    data-testid="article-cancel-edit"
                    className="mt-6 ml-4 inline-flex items-center gap-2 px-6 py-4 overline text-[#1a1918]/60 hover:text-[#ce1e20] transition-colors"
                  >
                    Annuler
                  </button>
                )}
              </div>

              <div className="divide-y divide-[#1a1918]/15 border-t border-[#1a1918]/25">
                {articles.map((a, i) => (
                  <div
                    key={a.id}
                    data-testid={`admin-article-${i}`}
                    className="py-5 flex items-center justify-between gap-4"
                  >
                    <div className="min-w-0">
                      <div className="overline text-[#ce1e20]">
                        {a.tag} · {a.date} · {a.readTime}
                      </div>
                      <div className="mt-1 font-serif-ed text-xl text-[#1a1918] truncate">
                        {a.title}
                      </div>
                    </div>
                    <div className="flex items-center gap-5 shrink-0">
                      <a
                        href={`/journal/${a.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        className="overline text-[#1a1918]/60 hover:text-[#1a1918] transition-colors"
                      >
                        Voir →
                      </a>
                      <button
                        onClick={() => startEdit(a)}
                        data-testid={`article-edit-${i}`}
                        className="overline text-[#0a1e38]/70 hover:text-[#0a1e38] transition-colors"
                      >
                        Modifier
                      </button>
                      <button
                        onClick={() => deleteArticle(a.id)}
                        data-testid={`article-delete-${i}`}
                        className="overline text-[#ce1e20] hover:text-[#a71518] transition-colors"
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {tab !== "journal" && rows.length === 0 && (
            <div className="py-16 text-center text-[#1a1918]/60 font-serif-ed italic text-2xl">
              Aucune entrée pour le moment.
            </div>
          )}
          {tab !== "journal" && rows.map((r, i) => (
            <article
              key={r.id}
              data-testid={`admin-row-${i}`}
              className="py-8 grid grid-cols-12 gap-4"
            >
              <div className="col-span-12 md:col-span-2 overline text-[#1a1918]/60">
                {new Date(r.created_at).toLocaleString("fr-FR", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </div>
              <div className="col-span-12 md:col-span-3">
                <div className="font-serif-ed text-2xl text-[#1a1918]">
                  {r.nom}
                </div>
                {r.societe && (
                  <div className="text-sm text-[#1a1918]/70">{r.societe}</div>
                )}
              </div>
              <div className="col-span-12 md:col-span-3 text-sm">
                <a
                  href={`mailto:${r.email}`}
                  className="ed-link text-[#1a1918]"
                >
                  {r.email}
                </a>
                {r.telephone && (
                  <div>
                    <a
                      href={`tel:${r.telephone}`}
                      className="ed-link text-[#1a1918]/80"
                    >
                      {r.telephone}
                    </a>
                  </div>
                )}
                {r.urgence && (
                  <div className="mt-2 inline-block overline text-[10px] px-2 py-1 border border-[#1a1918]/30">
                    Urgence : {r.urgence}
                  </div>
                )}
              </div>
              <div className="col-span-12 md:col-span-4 text-sm text-[#1a1918]/85 leading-relaxed">
                {r.message}
                {r.forme_juridique && (
                  <div className="mt-2 inline-block overline text-[10px] px-2 py-1 border border-[#ce1e20]/50 text-[#ce1e20]">
                    Création : {r.forme_juridique}
                  </div>
                )}
                {r.date_souhaitee && (
                  <div className="mt-2 overline text-[10px] text-[#1a1918]/60">
                    RDV souhaité :{" "}
                    {new Date(r.date_souhaitee).toLocaleDateString("fr-FR", {
                      day: "2-digit",
                      month: "long",
                      year: "numeric",
                    })}
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
